/**
 * MentionNotificationBell - Notification bell specifically for @mentions
 *
 * Shows:
 * - Bell icon with unread count badge
 * - Dropdown panel with recent mentions
 * - Auto-loads from useMentions hook
 */

import { useState, useRef, useEffect } from 'react';
import { Bell } from 'lucide-react';
import { useMentions } from '@/hooks/useMentions';
import { MentionNotificationPanel } from './MentionNotificationPanel';
import { getTenantIdSync } from '@/hooks/useTenantId';

interface MentionNotificationBellProps {
  userId: string;
  tenantId?: string;
}

export function MentionNotificationBell({
  userId,
  tenantId = getTenantIdSync(),
}: MentionNotificationBellProps) {
  const [showPanel, setShowPanel] = useState(false);
  const panelRef = useRef<HTMLDivElement>(null);
  const buttonRef = useRef<HTMLButtonElement>(null);

  const {
    mentions,
    unreadCount,
    loading,
    markAsRead,
    markAllAsRead,
    openMention,
    clearMention,
    clearAllMentions,
    clearReadMentions,
  } = useMentions({ userId, tenantId });

  // Close panel on outside click
  useEffect(() => {
    const handleClickOutside = (event: MouseEvent) => {
      if (
        panelRef.current &&
        buttonRef.current &&
        !panelRef.current.contains(event.target as Node) &&
        !buttonRef.current.contains(event.target as Node)
      ) {
        setShowPanel(false);
      }
    };

    if (showPanel) {
      document.addEventListener('mousedown', handleClickOutside);
      return () => document.removeEventListener('mousedown', handleClickOutside);
    }
  }, [showPanel]);

  const hasUnread = unreadCount > 0;

  return (
    <div className="relative">
      <button
        ref={buttonRef}
        onClick={() => setShowPanel(!showPanel)}
        className={`
          ${hasUnread ? 'bg-white/15 ring-2 ring-brand-secondary/40 shadow-md shadow-brand-secondary/10' : 'bg-white/[0.06] hover:bg-white/15'}
          p-2 rounded-lg transition-all active:scale-95 relative
        `}
        title={hasUnread ? `${unreadCount} unread mention${unreadCount > 1 ? 's' : ''}` : 'No mentions'}
      >
        <Bell
          className={`w-4 h-4 transition-colors ${
            hasUnread ? 'text-brand-secondary animate-pulse' : 'text-white/40'
          }`}
        />
        {hasUnread && (
          <span className="absolute -top-1 -right-1 min-w-[18px] h-[18px] px-1 bg-brand-secondary rounded-full border-2 border-brand-dark shadow-sm flex items-center justify-center">
            <span className="text-[9px] font-bold text-white">
              {unreadCount > 99 ? '99+' : unreadCount}
            </span>
          </span>
        )}
      </button>

      {/* Mention Panel Dropdown */}
      {showPanel && (
        <div
          ref={panelRef}
          className="absolute right-0 top-full mt-2 w-[380px] bg-surface-card border border-theme rounded-2xl shadow-2xl overflow-hidden z-50 backdrop-blur-xl"
        >
          {loading ? (
            <div className="p-8 text-center">
              <div className="animate-spin rounded-full h-6 w-6 border-b-2 border-brand-secondary mx-auto"></div>
              <p className="text-xs text-theme-muted mt-2">Loading mentions...</p>
            </div>
          ) : (
            <MentionNotificationPanel
              mentions={mentions}
              unreadCount={unreadCount}
              onMarkAsRead={markAsRead}
              onMarkAllAsRead={markAllAsRead}
              onOpenMention={(mention) => {
                openMention(mention).catch((err) => {
                  console.error('[MentionBell] openMention failed:', err);
                });
              }}
              onClear={(id) => {
                clearMention(id).catch((err) => {
                  // Hook rolls back optimistic update on failure; just log so
                  // we don't silently swallow diagnostic info.
                  console.error('[MentionBell] clearMention failed:', err);
                });
              }}
              onClearAll={() => {
                clearAllMentions().catch((err) => {
                  console.error('[MentionBell] clearAllMentions failed:', err);
                });
              }}
              onClearRead={() => {
                clearReadMentions().catch((err) => {
                  console.error('[MentionBell] clearReadMentions failed:', err);
                });
              }}
              onClose={() => setShowPanel(false)}
            />
          )}

          {/* Triangle pointer */}
          <div className="absolute -top-2 right-2 w-4 h-4 bg-surface-card border-t border-l border-theme transform rotate-45"></div>
        </div>
      )}
    </div>
  );
}
